import {
  type BaseRange,
  Editor,
  Element as SlateElement,
  Node as SlateNode,
  type Path,
  Path as SlatePath,
  type Point,
  Range as SlateRange,
  Text as SlateText,
} from 'slate';

import { type SavedDocumentAnchor } from 'src/data/savedDocument';

import { ANCHOR_CONTEXT_LENGTH, ANCHOR_MAX_GROWTH, ANCHOR_MIN_CONTEXT_LENGTH } from './const';

const PARAGRAPH_SEPARATOR = '\n';

export interface DocumentParagraph {
  index: number;
  path: Path;
  text: string;
  // Where the paragraph starts in `DocumentText.text`.
  start: number;
}

export interface DocumentText {
  text: string;
  paragraphs: DocumentParagraph[];
}

export interface ResolvedAnchor {
  range: BaseRange;
  // The paragraph the anchor resolved to now, which can differ from the stored `paragraphIndex`.
  paragraphIndex: number;
  // Document offsets of the quote, comparable between anchors.
  start: number;
  end: number;
}

const isTextBlock = (editor: Editor, node: SlateNode) =>
  SlateElement.isElement(node) &&
  Editor.isBlock(editor, node) &&
  node.children.every((child) => SlateText.isText(child) || Editor.isInline(editor, child));

/**
 * Paragraphs are the text blocks that hold some text, counted in document order. Empty blocks are
 * skipped, so adding or removing a blank line does not shift the indexes.
 */
export const getDocumentText = (editor: Editor): DocumentText => {
  const paragraphs: DocumentParagraph[] = [];
  let text = '';

  for (const [node, path] of Editor.nodes(editor, {
    at: [],
    match: (node) => isTextBlock(editor, node),
  })) {
    const paragraphText = SlateNode.string(node);
    if (!paragraphText.trim()) {
      continue;
    }
    if (paragraphs.length > 0) {
      text += PARAGRAPH_SEPARATOR;
    }
    paragraphs.push({ index: paragraphs.length, path, text: paragraphText, start: text.length });
    text += paragraphText;
  }

  return { text, paragraphs };
};

const findParagraphByPath = (document: DocumentText, path: Path) =>
  document.paragraphs.find((paragraph) => SlatePath.isAncestor(paragraph.path, path));

const findParagraphByOffset = (document: DocumentText, offset: number) =>
  [...document.paragraphs].reverse().find((paragraph) => paragraph.start <= offset);

const pointToOffset = (editor: Editor, document: DocumentText, point: Point) => {
  const paragraph = findParagraphByPath(document, point.path);
  if (!paragraph) {
    return null;
  }
  const before = Editor.string(editor, {
    anchor: Editor.start(editor, paragraph.path),
    focus: point,
  });
  return { paragraph, offset: paragraph.start + before.length };
};

const offsetToPoint = (
  editor: Editor,
  document: DocumentText,
  offset: number,
  { preferNextLeaf }: { preferNextLeaf: boolean }
): Point | null => {
  const paragraph = findParagraphByOffset(document, offset);
  if (!paragraph) {
    return null;
  }

  const block = SlateNode.get(editor, paragraph.path);
  const inParagraph = offset - paragraph.start;
  let passed = 0;
  let lastPoint: Point | null = null;

  for (const [text, relativePath] of SlateNode.texts(block)) {
    const end = passed + text.text.length;
    // A start on a leaf boundary belongs to the next leaf, so the range never opens on an empty
    // tail. An end on the boundary stays on the current leaf for the same reason.
    if (inParagraph < end || (inParagraph === end && !preferNextLeaf)) {
      return { path: [...paragraph.path, ...relativePath], offset: inParagraph - passed };
    }
    passed = end;
    lastPoint = { path: [...paragraph.path, ...relativePath], offset: text.text.length };
  }

  return lastPoint;
};

const countOccurrencesBefore = (text: string, quote: string, offset: number) => {
  let count = 0;
  for (
    let index = text.indexOf(quote);
    index !== -1 && index < offset;
    index = text.indexOf(quote, index + 1)
  ) {
    count += 1;
  }
  return count;
};

const isHighSurrogate = (code: number) => code >= 0xd800 && code <= 0xdbff;
const isLowSurrogate = (code: number) => code >= 0xdc00 && code <= 0xdfff;

// An emoji is two UTF-16 units. A lone half is not valid text, and a request that holds one fails.
// The context drops the half of a pair that it would cut.
const sliceWholeCharacters = (text: string, start: number, end: number) => {
  const from = start > 0 && isLowSurrogate(text.charCodeAt(start)) ? start + 1 : start;
  const to = end < text.length && isHighSurrogate(text.charCodeAt(end - 1)) ? end - 1 : end;
  return text.slice(from, to);
};

export const getAnchorFromRange = (
  editor: Editor,
  document: DocumentText,
  range: BaseRange
): SavedDocumentAnchor | null => {
  const [startPoint, endPoint] = SlateRange.edges(range);
  const start = pointToOffset(editor, document, startPoint);
  const end = pointToOffset(editor, document, endPoint);
  if (!start || !end) {
    return null;
  }

  const quote = document.text.slice(start.offset, end.offset);
  if (!quote.trim()) {
    return null;
  }

  const startInParagraph = start.offset - start.paragraph.start;
  const endInParagraph = end.offset - end.paragraph.start;

  return {
    quote,
    prefix: sliceWholeCharacters(
      start.paragraph.text,
      Math.max(0, startInParagraph - ANCHOR_CONTEXT_LENGTH),
      startInParagraph
    ),
    suffix: sliceWholeCharacters(
      end.paragraph.text,
      endInParagraph,
      endInParagraph + ANCHOR_CONTEXT_LENGTH
    ),
    paragraphIndex: start.paragraph.index,
    occurrence: countOccurrencesBefore(document.text, quote, start.offset),
  };
};

/**
 * The current position of a tracked range, in the same shape as a resolved anchor. Null when its
 * text is gone: the range collapsed, or it lies only in blank lines.
 */
export const getResolvedAnchorOfRange = (
  editor: Editor,
  document: DocumentText,
  range: BaseRange
): ResolvedAnchor | null => {
  if (SlateRange.isCollapsed(range)) {
    return null;
  }
  const [startPoint, endPoint] = SlateRange.edges(range);
  const start = pointToOffset(editor, document, startPoint);
  const end = pointToOffset(editor, document, endPoint);
  if (!start || !end || !document.text.slice(start.offset, end.offset).trim()) {
    return null;
  }
  return { range, paragraphIndex: start.paragraph.index, start: start.offset, end: end.offset };
};

export const isSameAnchor = (
  a: SavedDocumentAnchor | null | undefined,
  b: SavedDocumentAnchor | null | undefined
) =>
  (a ?? null) === (b ?? null) ||
  (Boolean(a) &&
    Boolean(b) &&
    a?.quote === b?.quote &&
    a?.prefix === b?.prefix &&
    a?.suffix === b?.suffix &&
    a?.paragraphIndex === b?.paragraphIndex &&
    a?.occurrence === b?.occurrence);

const countCommonSuffix = (a: string, b: string) => {
  let count = 0;
  while (
    count < a.length &&
    count < b.length &&
    a[a.length - 1 - count] === b[b.length - 1 - count]
  ) {
    count += 1;
  }
  return count;
};

const countCommonPrefix = (a: string, b: string) => {
  let count = 0;
  while (count < a.length && count < b.length && a[count] === b[count]) {
    count += 1;
  }
  return count;
};

// One side of the context still matches when at least half of it is unchanged. An empty side means
// the quote touched the paragraph's edge, so it matches only a quote that still does.
const isSideMatching = (stored: string, current: string, matched: number) =>
  stored.length === 0 ? current.length === 0 : matched * 2 >= stored.length;

interface Candidate {
  start: number;
  paragraph: DocumentParagraph;
  matching: boolean;
  sameOccurrence: boolean;
  context: number;
  distance: number;
}

/**
 * Candidates whose context still matches on one side win. Among them, the same occurrence of the
 * quote wins, then the one nearest the stored paragraph. Without a matching candidate, the best
 * context wins.
 * An edit above two identical copies shifts both paragraphs alike. Only the occurrence then tells
 * the copies apart.
 */
const isBetterCandidate = (candidate: Candidate, best: Candidate | null) => {
  if (!best) {
    return true;
  }
  if (candidate.matching !== best.matching) {
    return candidate.matching;
  }
  if (candidate.matching) {
    if (candidate.sameOccurrence !== best.sameOccurrence) {
      return candidate.sameOccurrence;
    }
    return (
      candidate.distance < best.distance ||
      (candidate.distance === best.distance && candidate.context > best.context)
    );
  }
  return (
    candidate.context > best.context ||
    (candidate.context === best.context && candidate.distance < best.distance)
  );
};

interface Match {
  start: number;
  end: number;
  paragraph: DocumentParagraph;
}

const findQuote = (document: DocumentText, anchor: SavedDocumentAnchor): Match | null => {
  let best: Candidate | null = null;
  let occurrence = -1;

  for (
    let start = document.text.indexOf(anchor.quote);
    start !== -1;
    start = document.text.indexOf(anchor.quote, start + 1)
  ) {
    occurrence += 1;
    const paragraph = findParagraphByOffset(document, start);
    const endParagraph = findParagraphByOffset(document, start + anchor.quote.length);
    if (!paragraph || !endParagraph) {
      continue;
    }

    const before = paragraph.text.slice(0, start - paragraph.start);
    const after = endParagraph.text.slice(start + anchor.quote.length - endParagraph.start);
    const prefixMatch = countCommonSuffix(before, anchor.prefix);
    const suffixMatch = countCommonPrefix(after, anchor.suffix);
    const candidate: Candidate = {
      start,
      paragraph,
      matching:
        isSideMatching(anchor.prefix, before, prefixMatch) ||
        isSideMatching(anchor.suffix, after, suffixMatch),
      sameOccurrence: occurrence === anchor.occurrence,
      context: prefixMatch + suffixMatch,
      distance: Math.abs(paragraph.index - anchor.paragraphIndex),
    };

    if (isBetterCandidate(candidate, best)) {
      best = candidate;
    }
  }

  return best
    ? { start: best.start, end: best.start + anchor.quote.length, paragraph: best.paragraph }
    : null;
};

const findAll = (text: string, search: string) => {
  const found: number[] = [];
  for (let index = text.indexOf(search); index !== -1; index = text.indexOf(search, index + 1)) {
    found.push(index);
  }
  return found;
};

export const countQuoteOccurrences = (document: DocumentText, quote: string) =>
  findAll(document.text, quote).length;

/**
 * The quote was edited, so it is not in the text any more: take the text between the stored prefix
 * and suffix instead, when both are still there and not much text came in between. An empty side
 * means the quote touched the paragraph's edge, so the edge stands in for it.
 */
const findBetweenContext = (document: DocumentText, anchor: SavedDocumentAnchor): Match | null => {
  const { prefix, suffix, quote } = anchor;
  if (
    (prefix && prefix.length < ANCHOR_MIN_CONTEXT_LENGTH) ||
    (suffix && suffix.length < ANCHOR_MIN_CONTEXT_LENGTH) ||
    (!prefix && !suffix)
  ) {
    return null;
  }

  // A missing side is the edge of the paragraph the other side is in, never of another paragraph.
  const pairs: [number, number][] = [];
  if (prefix && suffix) {
    const ends = findAll(document.text, suffix);
    findAll(document.text, prefix).forEach((index) =>
      ends.forEach((end) => pairs.push([index + prefix.length, end]))
    );
  } else if (suffix) {
    findAll(document.text, suffix).forEach((end) => {
      const paragraph = findParagraphByOffset(document, end);
      if (paragraph) {
        pairs.push([paragraph.start, end]);
      }
    });
  } else {
    findAll(document.text, prefix).forEach((index) => {
      const start = index + prefix.length;
      const paragraph = findParagraphByOffset(document, start);
      if (paragraph) {
        pairs.push([start, paragraph.start + paragraph.text.length]);
      }
    });
  }

  let best: (Match & { distance: number; growth: number }) | null = null;
  pairs.forEach(([start, end]) => {
    const growth = Math.abs(end - start - quote.length);
    const paragraph = findParagraphByOffset(document, start);
    if (end <= start || growth > ANCHOR_MAX_GROWTH || !paragraph) {
      return;
    }
    const distance = Math.abs(paragraph.index - anchor.paragraphIndex);
    if (!best || distance < best.distance || (distance === best.distance && growth < best.growth)) {
      best = { start, end, paragraph, distance, growth };
    }
  });
  return best;
};

/**
 * Finds the quote in the current text, using the stored context and paragraph to pick between
 * occurrences, and falls back to the text between the stored context when the quote was edited.
 * Returns null when neither is found; the comment or rating is then hidden.
 *
 * `exactOnly` skips the fallback, for text the editor saw being deleted: only the same words can
 * bring the anchor back, as an undo does.
 */
export const resolveAnchor = (
  editor: Editor,
  document: DocumentText,
  anchor: SavedDocumentAnchor,
  { exactOnly = false }: { exactOnly?: boolean } = {}
): ResolvedAnchor | null => {
  const match =
    findQuote(document, anchor) ?? (exactOnly ? null : findBetweenContext(document, anchor));
  if (!match) {
    return null;
  }

  const anchorPoint = offsetToPoint(editor, document, match.start, { preferNextLeaf: true });
  const focusPoint = offsetToPoint(editor, document, match.end, { preferNextLeaf: false });
  if (!anchorPoint || !focusPoint) {
    return null;
  }

  return {
    range: { anchor: anchorPoint, focus: focusPoint },
    paragraphIndex: match.paragraph.index,
    start: match.start,
    end: match.end,
  };
};
